import { useState } from "react";
import { Download, LogOut, Check } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { InstallSheet, isStandalone } from "@/components/InstallPrompt";

function Seg({ value, options, onChange, testid }) {
  return (
    <div className="flex rounded-xl bg-[#F5F0E6] p-1" data-testid={testid}>
      {options.map(([k, l]) => (
        <button key={k} onClick={() => onChange(k)} data-testid={`${testid}-${k}`}
          className={`flex-1 min-h-[40px] rounded-lg text-sm font-semibold transition-colors ${value === k ? "bg-white shadow-sm text-[#1E2B3C]" : "text-[#5C6B73]"}`}>{l}</button>
      ))}
    </div>
  );
}

export default function Settings() {
  const { user, updateProfile, logout, t } = useAuth();
  const [f, setF] = useState({ first_name: user.first_name, last_name: user.last_name || "", phone: user.phone || "" });
  const [install, setInstall] = useState(false);
  const set = async (patch) => {
    try { await updateProfile(patch); toast.success("Saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  const row = "sb-card p-5 space-y-3";
  return (
    <div className="max-w-2xl" data-testid="settings-page">
      <PageHeader eyebrow={t("Settings")} title={`${user.first_name} ${user.last_name || ""}`} subtitle={user.email} />
      <div className="space-y-4" id="prefs">
        <section className={row}><div className="sb-eyebrow">{t("Language")}</div>
          <Seg value={user.language || "en"} options={[["en", "English"], ["fr", "Français"]]} onChange={(v) => set({ language: v })} testid="settings-language" />
          <p className="text-xs text-[#8C9BA5]">Lessons and audio switch language. Your progress stays the same.</p></section>
        <section className={row}><div className="sb-eyebrow">{t("Lesson Mode")}</div>
          <Seg value={user.audio_mode || "audio"} options={[["audio", "Audio On"], ["read", "Read Only"]]} onChange={(v) => set({ audio_mode: v })} testid="settings-audio-mode" /></section>
        <section className={`${row} flex items-center justify-between space-y-0`}>
          <div><div className="sb-eyebrow">{t("Completion Sounds")}</div><div className="text-sm text-[#5C6B73] mt-1">Sound effects when you finish a lesson</div></div>
          <Switch checked={user.sound_effects !== false} onCheckedChange={(v) => set({ sound_effects: v })} data-testid="settings-sound-switch" />
        </section>
        <section className={`${row} flex items-center justify-between space-y-0`}>
          <div><div className="sb-eyebrow">PWA</div><div className="text-sm text-[#5C6B73] mt-1">{isStandalone() ? "Sidebizz is installed on this device" : "Add Sidebizz to your Home Screen"}</div></div>
          {isStandalone() ? <Check className="h-5 w-5 text-[#1DB99A]" /> : <button onClick={() => setInstall(true)} className="sb-btn-primary min-h-[40px] text-sm" data-testid="settings-install-btn"><Download className="h-4 w-4" />{t("Install Sidebizz")}</button>}
        </section>
        <section className={row} data-testid="settings-account">
          <div className="sb-eyebrow">{t("Account")}</div>
          <div className="grid grid-cols-2 gap-3">
            {[["first_name", "First name"], ["last_name", "Last name"], ["phone", "Phone"]].map(([k, l]) => (
              <label key={k} className={k === "phone" ? "col-span-2" : ""}><span className="text-xs font-semibold text-[#5C6B73]">{l}</span>
                <input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="mt-1 w-full h-11 rounded-xl border border-[#EADEC9] px-3" data-testid={`settings-${k}-input`} /></label>
            ))}
          </div>
          <button onClick={() => set(f)} className="sb-btn-navy min-h-[44px] text-sm" data-testid="settings-save-profile-btn">{t("Save")}</button>
          <dl className="grid grid-cols-2 gap-2 text-xs pt-3 border-t border-[#EADEC9]">
            {[["Plan", user.plan?.replace("_", " ")], ["Status", user.status], ["Joined", user.created_at?.slice(0, 10)], ["Access expiry", user.expiry_date || "—"]].map(([k, v]) => (
              <div key={k}><dt className="text-[#8C9BA5]">{k}</dt><dd className="font-semibold capitalize" data-testid={`settings-account-${k.toLowerCase().replace(" ", "-")}`}>{v}</dd></div>
            ))}
          </dl>
        </section>
        <button onClick={logout} className="sb-btn-ghost w-full text-[#E05A47]" data-testid="settings-logout-btn"><LogOut className="h-4 w-4" />{t("Logout")}</button>
      </div>
      <InstallSheet open={install} onClose={() => setInstall(false)} />
    </div>
  );
}
